/**
 * ChatObjetivo — a casca do onboarding como conversa com o VITA.
 * Fonte: handoff/redesign/Objetivo.dc.html e handoff/12-prompt-objetivo-vita.md.
 *
 * O QUE ELA NÃO FAZ: orquestrar. O `FormsObjetivo` continua dono de
 * `currentStep`, `answers`, `next`, `back`, do auto-advance e do
 * `saveUserObjective`. Esta casca recebe o step ATIVO como `children` e o
 * desenha no fim da conversa; os anteriores aparecem acima como histórico
 * (pergunta do VITA + bolha da resposta), lidos de `answers` — o MESMO objeto
 * que vai para o servidor, então a bolha nunca mostra algo diferente do que
 * foi salvo.
 *
 * O ROSTO É O MESMO DO CHAT: a foto de `VitaAvatar` (chat do VITA no app), e
 * não um orb desenhado em CSS. Um assistente com duas caras é dois
 * assistentes; o halo que pulsa ficou, porque é o que dá presença.
 *
 * AS ANIMAÇÕES, e por que cada uma existe:
 *  - halo do avatar: 2.4s, o único movimento permanente da tela.
 *  - `digitando…`: 3 pontos em bounce com 0/150/300ms de atraso. Cobre a
 *    janela entre a resposta e a pergunta seguinte — inclusive os 350ms do
 *    auto-advance, que antes eram um salto seco.
 *  - `ob-in` na pergunta ativa: translateY(10px) → 0 em 350ms. A pergunta
 *    "chega", não aparece.
 *  - `ob-pop` na bolha e no card de projeção: scale .96 → 1.
 *  - rolagem: `scroll-behavior: smooth` + auto-scroll no fim de cada mudança.
 *
 * A VIBRAÇÃO acontece quando o VITA TERMINA de digitar — o instante em que
 * uma mensagem chega. `haptics.light`, o mesmo peso do chat do VITA: é aviso,
 * não celebração. Silenciosa na web e sem plugin, por contrato do módulo.
 *
 * A duração do "digitando" é proporcional ao comentário que vem depois
 * (650ms + 6ms por caractere, teto de 1100ms) — o mesmo cálculo do protótipo.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

import haptics from '../../design/feedback/haptics';
import { VitaAvatar } from '../vita/chat/VitaChatScreens.jsx';
import { historicoDaConversa, perguntaDoStep } from './conversa';
import CardDeProjecao from './CardDeProjecao';
import s from './ChatObjetivo.module.scss';

/** Base e teto do "digitando…", em ms (§ 1 do prompt: 650–1100). */
const DIGITANDO_MIN = 650;
const DIGITANDO_MAX = 1100;
const MS_POR_CARACTERE = 6;

export default function ChatObjetivo({
  steps = [],
  indiceAtual = 0,
  answers = {},
  nome = '',
  onVoltar,
  onInicio,
  mostrarInicio = false,
  mostrarProgresso = true,
  rodape = null,
  children,
}) {
  const rolagem = useRef(null);
  const [digitando, setDigitando] = useState(false);
  const indiceAnterior = useRef(indiceAtual);

  const StepAtivo = steps[indiceAtual];
  const perguntaAtiva = perguntaDoStep(StepAtivo, nome);

  const historico = useMemo(
    () => historicoDaConversa({ steps, ate: indiceAtual, answers, nome }),
    [steps, indiceAtual, answers, nome]
  );

  /* Avançou de step: mostra "digitando…" antes da pergunta nova. Voltar não
     digita — desfazer é instantâneo, como apagar o que você escreveu. */
  useEffect(() => {
    const anterior = indiceAnterior.current;
    indiceAnterior.current = indiceAtual;
    if (indiceAtual <= anterior) {
      setDigitando(false);
      return undefined;
    }

    const comentario = historico[historico.length - 1]?.comentario || '';
    const espera = Math.min(
      DIGITANDO_MAX,
      DIGITANDO_MIN + comentario.length * MS_POR_CARACTERE
    );
    setDigitando(true);
    const t = setTimeout(() => {
      setDigitando(false);
      // A mensagem chegou: um toque curto, como no chat do VITA.
      haptics.light();
    }, espera);
    return () => clearTimeout(t);
  }, [indiceAtual, historico]);

  /* A conversa cresce para baixo: sem isto a pergunta nova nasce fora da tela. */
  useEffect(() => {
    const el = rolagem.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    // eslint-disable-next-line consistent-return
    return () => cancelAnimationFrame(id);
  }, [historico.length, digitando, indiceAtual]);

  const total = Math.max(1, steps.length - 1);
  const passo = Math.min(indiceAtual, total);
  const fracao = indiceAtual >= steps.length - 1 ? 1 : passo / total;

  return (
    <div className={s.tela}>
      {/* ── Cabeçalho: voltar, VITA, status e passo ── */}
      <div className={s.topo}>
        <div className={s.topoLinha}>
          <div className={s.navegacao}>
            {mostrarInicio && (
              <button
                type="button"
                className={s.voltar}
                onClick={onInicio}
                aria-label="Sair para o app"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M3.5 10.5 12 3.7l8.5 6.8v9.2a1 1 0 0 1-1 1h-5.2v-6h-4.6v6H4.5a1 1 0 0 1-1-1z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            <button
              type="button"
              className={s.voltar}
              onClick={onVoltar}
              aria-label="Voltar"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="m15 18-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className={s.identidade}>
            <span className={s.rosto}>
              <span className={s.halo} aria-hidden="true" />
              <VitaAvatar tamanho={34} borda={0} />
            </span>
            <span className={s.identidadeTexto}>
              <span className={s.vita}>VITA</span>
              <span className={s.status} aria-live="polite">
                {digitando ? 'digitando…' : 'seu coach'}
              </span>
            </span>
          </div>

          {mostrarProgresso && (
            <span className={s.passo}>{`${passo + 1} / ${total}`}</span>
          )}
        </div>

        <div className={s.trilha}>
          <span
            className={s.progresso}
            // Largura é dado derivado do passo: inline por obrigação.
            style={{ width: `${Math.round(fracao * 100)}%` }}
          />
        </div>
      </div>

      {/* ── A conversa ── */}
      <div
        className={`${s.conversa} ${rodape ? '' : s.conversaSemRodape}`}
        ref={rolagem}
      >
        {historico.map((linha) => (
          <div key={`${linha.id}-${linha.indice}`} className={s.bloco}>
            <span className={s.pergunta}>{linha.pergunta}</span>

            {linha.resposta && (
              <div className={s.linhaResposta}>
                <span className={s.bolha}>{linha.resposta}</span>
              </div>
            )}

            {linha.projecao && <CardDeProjecao answers={answers} />}

            {linha.comentario && <span className={s.comentario}>{linha.comentario}</span>}
          </div>
        ))}

        {digitando && (
          <div className={s.digitando} aria-hidden="true">
            <span /><span /><span />
          </div>
        )}

        {!digitando && StepAtivo && (
          <div className={s.atual}>
            {perguntaAtiva && <span className={s.perguntaAtiva}>{perguntaAtiva}</span>}
            {children}
          </div>
        )}
      </div>

      {/* ── Rodapé (CTA ou campo) ── */}
      {rodape && <div className={s.rodape}>{rodape}</div>}
    </div>
  );
}
